import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Section } from "@/components/layout/Section";
import { Calendar, MapPin, Clock, Ticket, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { motion } from "framer-motion";

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data));
  }, []);

  return (
    <div data-testid="events-page">
      <PageHeader
        kicker="Events"
        title="Come and encounter God with us."
        subtitle="From weekly gatherings to conferences and outreach — there's always something happening at CLBC."
      />
      <Section>
        <div className="grid lg:grid-cols-2 gap-6">
          {events.map((ev, i) => {
            const d = new Date(ev.starts_at);
            return (
              <motion.article
                key={ev.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="card-soft p-8"
                data-testid={`event-card-${i}`}
              >
                <div className="flex gap-6">
                  <div className="text-center bg-royal text-white rounded-2xl px-5 py-4 shadow-md min-w-[80px]">
                    <div className="text-[10px] uppercase tracking-widest text-gold">
                      {d.toLocaleString("en", { month: "short" })}
                    </div>
                    <div className="font-serif text-4xl leading-none mt-1">{d.getDate()}</div>
                    <div className="text-[10px] text-white/70 mt-1">
                      {d.toLocaleString("en", { weekday: "short" })}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl text-royal leading-snug">{ev.title}</h3>
                    <p className="mt-3 text-slate-600 leading-relaxed">{ev.description}</p>
                    <div className="mt-5 grid gap-2 text-sm text-slate-500">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gold" />
                        {d.toLocaleString("en", { hour: "numeric", minute: "2-digit" })}
                      </div>
                      {ev.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gold" /> {ev.location}
                        </div>
                      )}
                    </div>
                    {ev.registration_required && (
                      <Link
                        to={`/events/${ev.id}`}
                        className="btn-primary mt-6 text-sm inline-flex"
                        data-testid={`event-register-btn-${i}`}
                      >
                        <Ticket className="w-4 h-4" /> Register
                      </Link>
                    )}
                    <Link
                      to={`/events/${ev.id}`}
                      className="btn-ghost mt-3 !px-0 text-sm inline-flex"
                      data-testid={`event-details-link-${i}`}
                    >
                      View details <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
          {events.length === 0 && (
            <div className="col-span-full text-center py-20 text-slate-500 flex flex-col items-center gap-3">
              <Calendar className="w-10 h-10 text-gold" /> No events scheduled yet.
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
